import * as React from 'react';
import { Tooltip } from 'antd';
import {
  Bar,
  BarTileChartWrapper,
  RealtimeBarTileUnit,
  RealtimeBarTileValue,
  RealtimeNumber,
  ThresholdIndicator,
} from './styled';

import { Category, humanizeValue } from '../utils';
import { LOW_VALUE_STR } from '@utils/constants';

function showWidget(
  value: number | string | null,
  threshold: number | undefined | null,
  max: number,
  category: Category,
  greenBarEnabled: boolean,
) {
  const thresholdIndicator =
    typeof threshold === 'number' &&
    isFinite(threshold) &&
    [Category.Critical, Category.Warning].includes(category);

  let barMax = max;
  if (thresholdIndicator) {
    barMax = Math.max(max, threshold);
    if (barMax === threshold) {
      barMax = barMax * 1.1; // add 10% to the max
    }
  }

  const colorEnable = category === Category.Good ? greenBarEnabled : category !== Category.Default;

  const bar = colorEnable && typeof value === 'number' && barMax !== 0 && isFinite(barMax);
  return {
    bar,
    thresholdIndicator,
    barMax,
  };
}

export function BarTile({
  onClick,
  value,
  unit,
  category = Category.Good,
  threshold = 0,
  max = 0,
  isTotal = false,
  greenBarEnabled = true,
}) {
  let parsedCellData: { humanizedNumber: string; humanizedUnit: string };
  // mask low values
  if (value === LOW_VALUE_STR) {
    parsedCellData = {
      humanizedNumber: LOW_VALUE_STR,
      humanizedUnit: '',
    };
  }
  if (value === 'NA') {
    parsedCellData = {
      humanizedNumber: value,
      humanizedUnit: '',
    };
  } else {
    parsedCellData = humanizeValue(value, unit);
  }
  const { humanizedNumber: humanizedValue, humanizedUnit } = parsedCellData;
  const { humanizedNumber: humanizedThreshold, humanizedUnit: humanizedThresholdUnit } =
    humanizeValue(threshold, unit);

  const memoizedCallback = React.useCallback(() => onClick?.(), [onClick]);

  const { bar, thresholdIndicator, barMax } = showWidget(
    value,
    threshold,
    max,
    category,
    greenBarEnabled,
  );

  const title = humanizedThreshold
    ? `Threshold: ${humanizedThreshold} ${humanizedThresholdUnit}`
    : 'Threshold: NA';
  return (
    <Tooltip title={!isTotal && title}>
      <BarTileChartWrapper onClick={memoizedCallback}>
        {bar && !isTotal && <Bar $max={barMax} $value={value} $critical={category} />}
        <RealtimeNumber $critical={category}>
          <RealtimeBarTileValue>{humanizedValue}</RealtimeBarTileValue>
          <RealtimeBarTileUnit>{humanizedUnit}</RealtimeBarTileUnit>
        </RealtimeNumber>
        {thresholdIndicator && <ThresholdIndicator $max={barMax} $threshold={threshold} />}
      </BarTileChartWrapper>
    </Tooltip>
  );
}
